import { CenterSpinner } from "~/atoms/spinner";
import { type ReaderSummary } from "~/api/types";
import { useReaderReport } from "~/api/v2/useReaderReport";
import { deleteReader } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { toast } from "react-toastify";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "~/atoms/table";
import { Trash } from "lucide-react";
import { ConfirmDeletePopover } from "~/molecules/ConfirmDeletePopover";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import NoResult from "~/molecules/NoResult";
import ErrorState from "~/molecules/ErrorState";

interface Props {
  documentVersionId: number;
  isRemoveAllowed: boolean;
}

export const ReadersOverview = ({
  documentVersionId,
  isRemoveAllowed,
}: Props) => {
  const { t } = useTranslation();

  const readerTableColumnCount = 2 + (isRemoveAllowed ? 1 : 0);

  const [isRemoving, setIsRemoving] = useState<boolean>(false);
  const {
    data: readerReport,
    isLoading,
    error,
    mutate,
  } = useReaderReport(documentVersionId);

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (error) {
    return <ErrorState />;
  }

  if (!readerReport) {
    return <NoResult />;
  }

  const handleRemove = async (userId: number) => {
    try {
      setIsRemoving(true);
      await deleteReader(documentVersionId, userId);
      void mutate();
    } catch {
      toast.error(t("toasts.error.generic"));
    } finally {
      setIsRemoving(false);
    }
  };

  if (readerReport === undefined) {
    return <NoResult />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="text-sm text-gray-500">
            {t("commons.workflow.total")}
          </div>
          <div className="text-2xl font-bold">{readerReport.summary.total}</div>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="text-sm text-gray-500">
            {t("commons.workflow.opened")}
          </div>
          <div className="text-2xl font-bold">
            {readerReport.summary.opened}
          </div>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="text-sm text-gray-500">
            {t("commons.workflow.accepted")}
          </div>
          <div className="text-2xl font-bold">{readerReport.summary.read}</div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm">
        <Table className="table-fixed">
          <colgroup>
            <col className="w-auto" />
            <col className="w-28" />
            {isRemoveAllowed && <col className="w-16" />}
          </colgroup>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead>{t("commons.workflow.reader")}</TableHead>
              <TableHead className="whitespace-nowrap">
                {t("commons.workflow.status")}
              </TableHead>
              {isRemoveAllowed && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {readerReport.readers.length > 0 ? (
              readerReport.readers.map((reader: ReaderSummary) => (
                <TableRow key={reader.userId}>
                  <TableCell className="max-w-0">
                    <div className="flex min-w-0 items-center">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-900">
                          {reader.firstName} {reader.lastName}
                        </div>
                        <div
                          className="truncate text-sm text-gray-500"
                          title={reader.email}
                        >
                          {reader.email}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span
                      className={`inline-flex rounded-full px-2 text-xs leading-5 font-semibold ${
                        reader.hasRead
                          ? "bg-green-100 text-green-800"
                          : reader.hasOpened
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {reader.hasRead
                        ? t("commons.workflow.readerStatusRead")
                        : reader.hasOpened
                          ? t("commons.workflow.opened")
                          : t("commons.workflow.notOpened")}
                    </span>
                  </TableCell>
                  {isRemoveAllowed && (
                    <TableCell className="text-right">
                      <ConfirmDeletePopover
                        trigger={
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={isRemoving}
                          >
                            <Trash />
                          </Button>
                        }
                        onConfirm={() => handleRemove(reader.userId)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={readerTableColumnCount}
                  className="text-muted-foreground text-center italic"
                >
                  {t("commons.workflow.noReaders")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
